/**
 * TypeScript interfaces for IntegrationProfile JSONB fields
 *
 * These interfaces provide type-safe access to the `config_json` JSONB column
 * in the `integration_profiles` table.
 *
 * **Architecture Note**: Const arrays define enum values (single source of truth),
 * TypeScript types provide compile-time safety, and Zod schemas in
 * `integration-profile.schemas.ts` reference these const arrays for runtime validation.
 */
// ==================== SFTP DELIVERY TYPES ====================
/**
 * Known SFTP directory names used by convention.
 *
 * Notes:
 * - This list is intentionally small; `directories[]` is extensible.
 * - Validation should enforce required directories (e.g., `outbound`) at call sites.
 */
export const SFTP_DIRECTORY_NAME_VALUES = ['inbound', 'outbound', 'error', 'archive'];
// ==================== API DELIVERY TYPES ====================
/**
 * Supported API authentication strategies.
 *
 * Notes:
 * - `none` is suitable for internal/private endpoints.
 * - `api_key` supports API key style auth (typically via header).
 * - `bearer` and `basic` are expected to source credentials from `secretArn`.
 */
export const API_AUTH_TYPE_VALUES = ['none', 'api_key', 'bearer', 'basic'];
/**
 * Supported HTTP methods for API delivery.
 */
export const API_HTTP_METHOD_VALUES = ['GET', 'POST', 'PUT', 'PATCH', 'DELETE'];
// ==================== ACQUISITION TYPES ====================
/**
 * Acquisition dispatch mode.
 *
 * `hybrid` matches the agreed V1 approach:
 * - coarse scheduler trigger + per-config dispatch
 */
export const ACQUISITION_SCHEDULER_MODE_VALUES = ['hybrid'];
/**
 * Downstream handoff mode for acquired files.
 */
export const ACQUISITION_HANDOFF_MODE_VALUES = ['api_now', 'api_and_sqs', 'sqs_only'];
/**
 * Dedupe scope for checksum strategy.
 *
 * Notes:
 * - Default for V1 is `party_file_format`.
 * - Scope may be overridden per partner/file-format.
 * - `party_file_type` remains available for broader dedupe across multiple formats
 *   within the same file-type lane.
 */
export const ACQUISITION_DEDUPE_SCOPE_VALUES = [
    'global',
    'party',
    'party_file_type',
    'party_file_format',
];
/**
 * Remote post-acquisition action policy.
 */
export const ACQUISITION_POST_ACTION_MODE_VALUES = [
    'platform_delete',
    'remote_managed',
    'no_action',
];
/**
 * File-level failure handling mode.
 */
export const ACQUISITION_FAILURE_MODE_VALUES = ['fail_fast', 'best_effort'];
//# sourceMappingURL=integration-profile.types.js.map